import { BodyText, Col } from '@policygenius/mortar';
import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import { WAITING_PERIOD_PATH } from 'routes/paths';

import { HEADING, LIST_ITEMS } from './constants';
import BenefitAmountForm from './form';
import { BenefitAmountFormValues } from './form/types';
import { useGetBenefitAmountQuery } from './graphql/GetBenefitAmount';
import { useUpdateBenefitAmountMutation } from './graphql/UpdateBenefitAmount';
import styles from './styles';
import buildInitialValues from './utils/buildInitialValues';
import { getValidator } from './utils/validate';

const useStyles = createUseStyles(styles, { name: 'BenefitAmount' });

const BenefitAmount = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.BenefitAmount);
  const { data, loading } = useGetBenefitAmountQuery({
    fetchPolicy: 'cache-and-network',
  });
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const minimumBenefit = data?.surveyBenefitAmount.minimumBenefit ?? 0;
  const maximumBenefit = data?.surveyBenefitAmount.maximumBenefit ?? 0;
  const validate = useMemo(
    () =>
      data
        ? getValidator({
            min: minimumBenefit,
            max: maximumBenefit,
          })
        : undefined,
    [minimumBenefit, maximumBenefit]
  );
  const history = useHistory();

  const [updateBenefitAmount] = useUpdateBenefitAmountMutation({
    onCompleted: () => {
      trackStepCompleted();
      history.push(WAITING_PERIOD_PATH);
    },
  });

  const onSubmit = async (values: BenefitAmountFormValues) => {
    await updateBenefitAmount({
      variables: {
        input: values,
      },
    });
  };

  const classes = useStyles();

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} title={Steps.BenefitAmount}>
      <Col push={{ small: 0, medium: 2, large: 3 }} span={{ small: 4, medium: 8, large: 6 }}>
        <BodyText size="large">
          The{' '}
          <BodyText as="strong" weight="bold" size="inherit">
            benefit amount
          </BodyText>{' '}
          on your policy is the monthly amount the policy pays you during a disability
        </BodyText>
        <ul className={classes.list}>
          {LIST_ITEMS.map((item) => (
            <BodyText key={item} as="li">
              {item}
            </BodyText>
          ))}
        </ul>
      </Col>
      <Form<BenefitAmountFormValues, InitialValues<BenefitAmountFormValues>>
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
      >
        {(formProps) => <BenefitAmountForm {...formProps} maximumBenefit={maximumBenefit} />}
      </Form>
    </NavigatorContent>
  );
};

export default BenefitAmount;
